import type { MapInstance, MarkerInstance } from "../../types/map";

export type MarkerRenderingMode = "webgl" | "html";

export interface ManagedMarkerOptions {
    coordinates: [number, number];
    icon: string;
    size: [number, number];
    anchor: [number, number];
    opacity: number;
    userData?: unknown;
}

interface HtmlMarkerInstance {
    setCoordinates(coordinates: number[]): HtmlMarkerInstance;
    setAnchor(anchor: number[]): HtmlMarkerInstance;
    destroy(): void;
}

interface MarkerConstructor {
    new (map: MapInstance, options: ManagedMarkerOptions): MarkerInstance;
}

interface HtmlMarkerConstructor {
    new (
        map: MapInstance,
        options: {
            coordinates: number[];
            html: HTMLElement;
            anchor?: number[];
            interactive?: boolean;
            preventMapInteractions?: boolean;
            userData?: unknown;
        }
    ): HtmlMarkerInstance;
}

export interface MarkerFactoryMapGL {
    Marker: MarkerConstructor;
    HtmlMarker?: HtmlMarkerConstructor;
}

const updateHtmlMarkerElement = (
    element: HTMLElement,
    options: Pick<ManagedMarkerOptions, "icon" | "size" | "opacity">
) => {
    const [width, height] = options.size;
    element.style.width = `${width}px`;
    element.style.height = `${height}px`;
    element.style.opacity = String(options.opacity);
    element.style.pointerEvents = options.opacity > 0 ? "auto" : "none";

    const img = element.querySelector("img");
    if (img instanceof HTMLImageElement) {
        img.src = options.icon;
        img.width = width;
        img.height = height;
    }
};

export const createHtmlMarkerElement = (
    options: Pick<ManagedMarkerOptions, "icon" | "size" | "opacity">
): HTMLElement => {
    const element = document.createElement("div");
    element.style.display = "block";
    element.style.cursor = "pointer";
    element.style.userSelect = "none";
    element.style.webkitUserSelect = "none";
    element.style.touchAction = "manipulation";

    const img = document.createElement("img");
    img.src = options.icon;
    img.alt = "";
    img.draggable = false;
    img.style.display = "block";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.pointerEvents = "none";

    element.appendChild(img);
    updateHtmlMarkerElement(element, options);

    return element;
};

class HtmlMarkerAdapter implements MarkerInstance {
    userData?: unknown;
    private readonly htmlMarker: HtmlMarkerInstance;
    private readonly element: HTMLElement;

    constructor(
        htmlMarker: HtmlMarkerInstance,
        element: HTMLElement,
        userData?: unknown
    ) {
        this.htmlMarker = htmlMarker;
        this.element = element;
        this.userData = userData;
    }

    setIcon(options: { icon: string; size?: [number, number]; anchor?: [number, number]; opacity?: number }): void {
        const currentWidth = parseFloat(this.element.style.width) || 46;
        const currentHeight = parseFloat(this.element.style.height) || 51;
        const currentOpacity = parseFloat(this.element.style.opacity) || 1;

        updateHtmlMarkerElement(this.element, {
            icon: options.icon,
            size: options.size ?? [currentWidth, currentHeight],
            opacity: options.opacity ?? currentOpacity,
        });

        if (options.anchor) {
            this.htmlMarker.setAnchor(options.anchor);
        }
    }

    setCoordinates(coordinates: [number, number]): void {
        this.htmlMarker.setCoordinates(coordinates);
    }

    setOpacity(opacity: number): void {
        updateHtmlMarkerElement(this.element, {
            icon: this.element.querySelector("img")?.getAttribute("src") || "",
            size: [
                parseFloat(this.element.style.width) || 46,
                parseFloat(this.element.style.height) || 51,
            ],
            opacity,
        });
    }

    destroy(): void {
        this.htmlMarker.destroy();
    }

    on(event: string, callback: (...args: unknown[]) => void): void {
        this.element.addEventListener(event, (domEvent) => {
            domEvent.stopPropagation();
            callback(domEvent);
        });
    }
}

export const createManagedMarker = (
    mapgl: MarkerFactoryMapGL,
    map: MapInstance,
    options: ManagedMarkerOptions,
    mode: MarkerRenderingMode
): MarkerInstance => {
    if (mode === "html" && mapgl.HtmlMarker) {
        const element = createHtmlMarkerElement(options);
        const htmlMarker = new mapgl.HtmlMarker(map, {
            coordinates: options.coordinates,
            html: element,
            anchor: options.anchor,
            interactive: true,
            preventMapInteractions: true,
            userData: options.userData,
        });

        return new HtmlMarkerAdapter(htmlMarker, element, options.userData);
    }

    return new mapgl.Marker(map, options);
};
